// Location utility functions for the chat app

/**
 * Get user's current location using Geolocation API
 * @returns {Promise<{latitude: number, longitude: number, accuracy: number}>}
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000 // Cache location for 1 minute
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        });
      },
      (error) => {
        let errorMessage = 'Unknown location error';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        reject(new Error(errorMessage));
      },
      options
    );
  });
};

/**
 * Get address from coordinates using Nominatim (OpenStreetMap) reverse geocoding
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<string>}
 */
export const getAddressFromCoordinates = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
    );
    
    const data = await response.json();
    
    if (data && data.display_name) {
      return data.display_name;
    } else {
      throw new Error('Unable to get address for this location');
    }
  } catch (error) {
    console.error('Error getting address:', error);
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }
};

/**
 * Search for locations using Nominatim
 * @param {string} query 
 * @returns {Promise<Array>}
 */
export const searchLocations = async (query) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`
    );
    
    const data = await response.json();
    
    return data.map(item => ({
      latitude: parseFloat(item.lat),
      longitude: parseFloat(item.lon),
      address: item.display_name,
      type: item.type,
      importance: item.importance
    }));
  } catch (error) {
    console.error('Error searching locations:', error);
    return [];
  }
};

/**
 * Calculate distance between two coordinates in kilometers
 * @param {number} lat1 
 * @param {number} lon1 
 * @param {number} lat2 
 * @param {number} lon2 
 * @returns {number}
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in kilometers
  return Math.round(distance * 100) / 100; // Round to 2 decimal places
};

/**
 * Format location data for message sending
 * @param {Object} location - Location object with latitude, longitude, etc.
 * @param {string} address - Optional address string
 * @returns {Object}
 */
export const formatLocationForMessage = (location, address = null) => {
  return {
    type: 'location',
    latitude: location.latitude,
    longitude: location.longitude,
    accuracy: location.accuracy,
    address: address || `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`,
    timestamp: location.timestamp || new Date().toISOString()
  };
};

/**
 * Generate OpenStreetMap URL for a location
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {string}
 */
export const generateMapsUrl = (latitude, longitude) => {
  return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=16/${latitude}/${longitude}`;
};

/**
 * Generate Google Maps URL for a location (fallback option)
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {string}
 */
export const generateGoogleMapsUrl = (latitude, longitude) => {
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

/**
 * Check if location permissions are granted
 * @returns {Promise<boolean>}
 */
export const checkLocationPermission = async () => {
  if (!navigator.permissions) {
    return false;
  }
  
  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' });
    return permission.state === 'granted';
  } catch (error) {
    console.error('Error checking location permission:', error);
    return false;
  }
};

/**
 * Request location permission
 * @returns {Promise<boolean>}
 */
export const requestLocationPermission = async () => {
  try {
    const location = await getCurrentLocation();
    return true;
  } catch (error) {
    console.error('Location permission denied:', error);
    return false;
  }
};

/**
 * Get tile layer URL for Leaflet map
 * @returns {string}
 */
export const getTileLayerUrl = () => {
  return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
};

/**
 * Get tile layer attribution for OpenStreetMap
 * @returns {string}
 */
export const getTileLayerAttribution = () => {
  return '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
};
